import React, { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, Star, ChevronRight, Award, Sparkles } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { listApps } from "@/lib/api";
import AdSlot from "@/components/AdSlot";
import { BEST_OF_TOPICS } from "@/constants/seoLandings";

const BASE_URL = "https://horseappcompare.com";

const setPageMeta = ({ title, description, url }) => {
  document.title = title;
  const set = (n, v, attr = "name") => {
    let el = document.querySelector(`meta[${attr}="${n}"]`);
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute(attr, n);
      document.head.appendChild(el);
    }
    el.setAttribute("content", v);
  };
  set("description", description);
  set("og:title", title, "property");
  set("og:description", description, "property");
  set("og:url", url, "property");
  set("og:type", "article", "property");
  let canonical = document.querySelector("link[rel='canonical']");
  if (!canonical) {
    canonical = document.createElement("link");
    canonical.setAttribute("rel", "canonical");
    document.head.appendChild(canonical);
  }
  canonical.setAttribute("href", url);
};

const BestOfPage = () => {
  const { topic } = useParams();
  const { lang } = useLang();
  const [apps, setApps] = useState([]);

  const topicConfig = BEST_OF_TOPICS[topic];
  useEffect(() => {
    listApps({ limit: 100 }).then(setApps).catch(() => setApps([]));
  }, []);

  const c = topicConfig ? (topicConfig[lang] || topicConfig.en) : null;
  const url = `${BASE_URL}/best/${topic}`;
  const title = c?.title || "Best Horse Apps";
  const description = c?.lede || "Best horse apps";
  useEffect(() => {
    setPageMeta({ title, description, url });
  }, [title, description, url]);

  if (!topicConfig) return <Navigate to="/best-horse-apps" replace />;

  const bySlug = (slug) => apps.find((a) => a.slug === slug);
  const picks = c.slugs.map(bySlug).filter(Boolean);
  const isNl = lang === "nl";

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${BASE_URL}/` },
      { "@type": "ListItem", position: 2, name: c.h1, item: url },
    ],
  };
  const itemListLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: c.h1,
    numberOfItems: picks.length,
    itemListElement: picks.map((a, i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: {
        "@type": "SoftwareApplication",
        name: a.name,
        operatingSystem: [a.platforms?.ios && "iOS", a.platforms?.android && "Android", a.platforms?.web && "Web"].filter(Boolean).join(", "),
        applicationCategory: "LifestyleApplication",
        offers: { "@type": "Offer", price: a.pricing?.monthly_usd || 0, priceCurrency: "USD" },
        aggregateRating: { "@type": "AggregateRating", ratingValue: a.rating, reviewCount: a.review_count || 1 },
        url: `${BASE_URL}/apps/${a.slug}`,
      },
    })),
  };

  return (
    <main>
      <section className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
        <div className="hac-container py-12 md:py-20 max-w-5xl">
          <nav className="flex items-center gap-2 text-xs text-[#64748B] mb-6">
            <Link to="/" className="hover:text-[#2563EB]">Home</Link>
            <ChevronRight size={12} />
            <Link to="/best-horse-apps" className="hover:text-[#2563EB]">
              {isNl ? "Beste Paardenapps" : "Best Horse Apps"}
            </Link>
            <ChevronRight size={12} />
            <span className="text-[#0F172A] font-medium">{c.h1}</span>
          </nav>
          <div className="overline mb-3 text-[#2563EB]">
            <Award size={12} className="inline mr-2" /> {isNl ? "Editorial keuze" : "Editorial pick"}
          </div>
          <h1 className="font-heading text-4xl md:text-6xl text-[#0F172A] leading-[1.05] mb-5">
            {c.h1}
          </h1>
          <p className="text-lg text-[#64748B] leading-relaxed max-w-3xl mb-6">{c.lede}</p>
          <p className="text-[#475569] leading-relaxed max-w-3xl">{c.intro}</p>
        </div>
      </section>

      <AdSlot placement="category-banner" />

      <section className="hac-container py-16 max-w-5xl">
        <h2 className="font-heading text-2xl md:text-4xl text-[#0F172A] mb-8">
          {isNl ? `Top ${picks.length}` : `Top ${picks.length}`}
        </h2>
        <div className="space-y-4">
          {picks.map((a, i) => (
            <Link key={a.slug} to={`/apps/${a.slug}`}
              className="flex items-start gap-5 p-5 md:p-6 border border-[#E2E8F0] rounded-2xl bg-white hover:border-[#2563EB] hover:shadow-md transition-all">
              <div className="font-mono text-3xl text-[#94A3B8] w-12 shrink-0 pt-1">0{i + 1}</div>
              <span className="w-14 h-14 shrink-0 flex items-center justify-center text-white font-bold text-xl rounded-xl"
                style={{ backgroundColor: a.color || "#2563EB" }}>{a.name.charAt(0)}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap mb-1">
                  <h3 className="font-bold text-xl text-[#0F172A]">{a.name}</h3>
                  <span className="inline-flex items-center gap-1 text-sm text-[#64748B]">
                    <Star size={14} className="fill-[#2563EB] text-[#2563EB]" />{a.rating?.toFixed(1)}
                  </span>
                  <span className="text-xs text-[#94A3B8]">{a.country}</span>
                </div>
                <p className="text-sm text-[#64748B] leading-relaxed">{a.tagline}</p>
                <div className="flex items-center gap-3 mt-3 flex-wrap">
                  {(a.target_audience || []).slice(0, 3).map((aud) => (
                    <span key={aud} className="hac-chip text-xs">{aud}</span>
                  ))}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-heading text-2xl text-[#0F172A]">
                  {a.pricing?.free_tier && a.pricing?.monthly_usd === 0
                    ? (isNl ? "Gratis" : "Free")
                    : `$${a.pricing?.monthly_usd}`}
                </div>
                {a.pricing?.monthly_usd > 0 && (
                  <div className="text-xs text-[#64748B]">{isNl ? "/mnd" : "/mo"}</div>
                )}
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="hac-container pb-16 max-w-4xl text-center">
        <div className="bg-[#EFF6FF] border border-[#DBEAFE] rounded-2xl p-8 md:p-12">
          <h2 className="font-heading text-2xl md:text-3xl text-[#0F172A] mb-3">
            {isNl ? "Wil je een persoonlijke aanbeveling?" : "Want a personalised recommendation?"}
          </h2>
          <p className="text-[#64748B] mb-6 max-w-2xl mx-auto">
            {isNl
              ? "Onze adviseur kiest 2-4 apps op basis van jouw situatie."
              : "Our advisor picks 2-4 apps based on your exact situation."}
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/advisor" className="hac-btn-primary">
              <Sparkles size={16} /> {isNl ? "Vraag de adviseur" : "Ask the advisor"}
            </Link>
            <Link to="/best-horse-apps" className="hac-btn-secondary">
              {isNl ? "Beste paardenapps overzicht" : "Best horse apps overview"} <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListLd) }} />
    </main>
  );
};

export default BestOfPage;
